import { useState, useEffect, useCallback } from "react";
import { Search, Filter, Download, RefreshCw, Loader2, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { tokenStore } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const severityStyle = (s) =>
  s === "critical" ? "bg-red-100 text-red-700 border-red-300 font-bold" :
  s === "warning"  ? "bg-amber-50 text-amber-700 border-amber-200" :
                     "bg-slate-50 text-slate-600 border-slate-200";

const isFlushEvent = (log) => log.event_type === "entropy_pool_flush" || log.event === "ENTROPY_POOL_FLUSH";

export default function AuditLogs() {
  const { t } = useLanguage();
  const [logs, setLogs]                 = useState([]);
  const [stats, setStats]               = useState({ total: 0, warnings: 0, critical: 0, info: 0 });
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [severityFilter, setSeverityFilter] = useState("all-severity");
  const [eventTypeFilter, setEventTypeFilter] = useState("all-types");

  const buildUrl = useCallback(() => {
    const params = new URLSearchParams({ limit: "100" });
    if (eventTypeFilter !== "all-types") params.set("event_type", eventTypeFilter);
    return `${API}/api/v1/audit-logs/?${params}`;
  }, [eventTypeFilter]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(buildUrl(), {
        headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setStats({ total: data.total, warnings: data.warnings, critical: data.critical, info: data.info });
      }
    } catch { /* keep existing data */ }
    finally { setLoading(false); }
  }, [buildUrl]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filtered = logs.filter(log => {
    const matchSearch =
      !search ||
      log.event.includes(search.toUpperCase()) ||
      log.actor.includes(search) ||
      log.details.toLowerCase().includes(search.toLowerCase()) ||
      (log.target && log.target.toLowerCase().includes(search.toLowerCase()));
    const matchSeverity = severityFilter === "all-severity" || log.severity === severityFilter;
    return matchSearch && matchSeverity;
  });

  const handleExport = () => {
    const csv = [
      "Timestamp,Severity,Event,Actor,Target,IP,Details",
      ...filtered.map(l =>
        `"${l.timestamp}","${l.severity}","${l.event}","${l.actor}","${l.target}","${l.ip}","${l.details}"`
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "audit-logs.csv";
    a.click();
  };

  return (
    <div data-testid="audit-logs-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="audit-logs-title">
            {t("audit.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t("audit.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-xs px-4 py-2"
            data-testid="export-logs-btn"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-xs px-4 py-2 disabled:opacity-50"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        {[
          { label: "Total Events",  value: loading ? "…" : stats.total.toLocaleString(), testid: "stat-total" },
          { label: "Critical",      value: loading ? "…" : stats.critical,               testid: "stat-critical", highlight: stats.critical > 0 },
          { label: "Warnings",      value: loading ? "…" : stats.warnings,               testid: "stat-warnings" },
          { label: "Info",          value: loading ? "…" : stats.info.toLocaleString(),  testid: "stat-info" },
        ].map((s) => (
          <div
            key={s.label}
            className={`bg-white border p-3 ${s.highlight ? "border-red-200 bg-red-50" : "border-slate-200"}`}
            data-testid={s.testid}
          >
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</div>
            <div className={`text-lg font-bold font-mono ${s.highlight ? "text-red-700" : "text-slate-900"}`}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4" data-testid="log-filters">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search events, actors, details..."
            className="pl-9 h-9 bg-white border-slate-200 text-sm"
            data-testid="log-search"
          />
        </div>

        {/* Event type filter */}
        <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
          <SelectTrigger className="w-48 h-9 text-xs" data-testid="log-event-type-filter">
            <SelectValue placeholder="Event type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-types">All Event Types</SelectItem>
            <SelectItem value="entropy_pool_flush">
              <span className="flex items-center gap-1.5">
                <Zap className="w-3 h-3 text-red-500" />
                Entropy Pool Flush
              </span>
            </SelectItem>
            <SelectItem value="api_key">API Key Events</SelectItem>
            <SelectItem value="auth">Authentication</SelectItem>
          </SelectContent>
        </Select>

        {/* Severity filter */}
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="log-severity-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-severity">All Severity</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>

        {/* Active filter badge */}
        {eventTypeFilter === "entropy_pool_flush" && (
          <span className="flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-full" data-testid="flush-filter-badge">
            <Zap className="w-3 h-3" /> Showing Pool Flush events only
          </span>
        )}
      </div>

      {/* Table */}
      <div data-testid="logs-table">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading audit logs...
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-[160px_80px_1fr_150px_150px_1fr] bg-slate-50 border-b border-slate-200">
              {["Timestamp", "Severity", "Event", "Actor", "Target", "Details"].map(h => (
                <div key={h} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
              ))}
            </div>
            {filtered.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">No audit logs found</div>
            ) : (
              filtered.map((log, i) => {
                const flush = isFlushEvent(log);
                return (
                  <div
                    key={log.id || i}
                    className={`grid grid-cols-[160px_80px_1fr_150px_150px_1fr] border-b border-slate-100 transition-colors
                      ${flush ? "bg-red-50/60 hover:bg-red-50 border-l-2 border-l-red-400" : "hover:bg-slate-50/50"}`}
                    data-testid={`log-row-${i}`}
                  >
                    <div className="px-4 py-3 font-mono text-[11px] text-slate-500 whitespace-nowrap flex items-center">
                      {log.timestamp
                        ? new Date(log.timestamp).toLocaleString("en-US", {
                            hour12: false, month: "short", day: "2-digit",
                            hour: "2-digit", minute: "2-digit", second: "2-digit",
                          })
                        : "—"}
                    </div>
                    <div className="px-4 py-3 flex items-center">
                      <Badge variant="outline" className={`text-[9px] uppercase tracking-wider ${severityStyle(log.severity)}`}>
                        {log.severity}
                      </Badge>
                    </div>
                    <div className="px-4 py-3 font-mono text-[11px] font-medium flex items-center gap-1.5">
                      {flush && <Zap className="w-3 h-3 text-red-500 shrink-0" />}
                      <span className={flush ? "text-red-700" : "text-slate-900"}>{log.event}</span>
                    </div>
                    <div className="px-4 py-3 text-xs text-slate-600 truncate flex items-center" title={log.actor}>
                      {log.actor}
                    </div>
                    <div className="px-4 py-3 font-mono text-[11px] text-slate-600 truncate flex items-center" title={log.target}>
                      {log.target || "—"}
                    </div>
                    <div className="px-4 py-3 text-xs text-slate-500 truncate flex items-center" title={log.details}>
                      {log.details || "—"}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
